import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, BookOpen, MessageCircle, ExternalLink, X } from 'lucide-react';

/**
 * HelpMenu — global header button that opens a small dropdown with:
 *   • "Open User Manual"       → navigates to /user-manual (in-app)
 *   • "User Manual in new tab" → external-style link to /user-manual (same SPA)
 *   • "Open Support Chat"      → navigates to /support
 *
 * Placed in AppLayout's desktop header and (compact variant) in the mobile header.
 */
export default function HelpMenu({ compact = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const openInNewTab = (path) => {
    setOpen(false);
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative inline-flex items-center justify-center rounded-lg transition-colors ${
          compact ? 'p-1.5 hover:bg-white/5' : 'p-2 hover:bg-white/5'
        }`}
        aria-label="Help & User Manual"
        title="Help & User Manual"
        data-testid="help-menu-trigger"
      >
        <HelpCircle
          className={compact ? 'w-5 h-5' : 'w-5 h-5'}
          style={{ color: open ? '#1E88E5' : '#A0AEC0' }}
          strokeWidth={2}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-xl shadow-2xl overflow-hidden z-50"
          style={{ background: '#0F1923', border: '1px solid #2D3748' }}
          data-testid="help-menu-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#2D3748' }}>
            <div className="flex items-center gap-2">
              <HelpCircle className="w-4 h-4" style={{ color: '#1E88E5' }} />
              <span className="text-sm font-semibold text-white">Help</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-md hover:bg-white/5"
              aria-label="Close"
              data-testid="help-menu-close"
            >
              <X className="w-4 h-4" style={{ color: '#64748B' }} />
            </button>
          </div>

          <div className="p-1">
            <button
              onClick={() => go('/user-manual')}
              className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-white/5 transition-colors"
              data-testid="help-open-manual"
            >
              <BookOpen className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#1E88E5' }} />
              <div>
                <p className="text-sm font-medium text-white">Open User Manual</p>
                <p className="text-xs" style={{ color: '#64748B' }}>Full guide to every feature</p>
              </div>
            </button>

            <button
              onClick={() => openInNewTab('/user-manual')}
              className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-white/5 transition-colors"
              data-testid="help-manual-new-tab"
            >
              <ExternalLink className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#00C896' }} />
              <div>
                <p className="text-sm font-medium text-white">Manual in New Tab</p>
                <p className="text-xs" style={{ color: '#64748B' }}>Keep it open while you work</p>
              </div>
            </button>

            <button
              onClick={() => go('/support')}
              className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-white/5 transition-colors"
              data-testid="help-open-support"
            >
              <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
              <div>
                <p className="text-sm font-medium text-white">Contact Support</p>
                <p className="text-xs" style={{ color: '#64748B' }}>Reach the Hines Time team</p>
              </div>
            </button>
          </div>

          <div className="px-4 py-2.5 border-t text-[11px]" style={{ borderColor: '#2D3748', color: '#64748B' }}>
            Manual also lives in <strong className="text-slate-300">Settings → User Manual</strong>
          </div>
        </div>
      )}
    </div>
  );
}
